import { definePreset } from '@primeuix/themes';
import { CarbonPreset, CARBON_PRIMARY } from './carbon.preset';
import { CARBON_CHART_COLORS } from './chart-palette';
import { tonalPalette } from './palette';

/**
 * White-label clients, laid over the Carbon theme.
 *
 * A client brings its primary colour — buttons, links, focus rings and selections follow it —
 * and, when it has them, its own chart colours and typeface; everything else is Carbon. Colours
 * are used exactly as the client gave them.
 *
 * The logo is not listed here: each client build copies its own `images/logo/logo-h.webp` from
 * `assets/clients/<client>/` over the default one, so the app always loads the same path.
 *
 * A build picks its entry from `environment.client.name`.
 */

/** A typeface served with the app, one file per face: `<path>/<family>-<Style>.woff2|woff|ttf`. */
export interface BrandFont {
    family: string;
    path: string;
}

export interface ClientBrand {
    /** A #rrggbb colour: step 500 of the primary palette, the lighter and darker steps derive from it. */
    primary: string;
    /** Categorical chart colours, assigned in order. */
    chartColors: readonly string[];
    font: BrandFont;
}

/** Poppins. The global stylesheet declares it (`styles/_fonts.scss`); the PDF reports load its .ttf files. */
export const CARBON_FONT: BrandFont = { family: 'Poppins', path: 'assets/layout/fonts' };

type ClientEntry = Pick<ClientBrand, 'primary'> & Partial<ClientBrand>;

export const CLIENTS = {
    Dcarbonize: { primary: CARBON_PRIMARY },
    Aktantis: { primary: '#001489' },
    Belfius: {
        primary: '#c30045',
        chartColors: [
            '#c30045',
            '#f6b2a4',
            '#e43755',
            '#ef7f82',
            '#710e35',
            '#e61d34',
            '#a83b5d',
            '#f993ad',
            '#ff3c76',
            '#940c33',
        ],
        font: { family: 'Belfius21', path: 'assets/fonts' },
    },
    Denuo: { primary: '#411a6f' },
    Effectis: { primary: '#03359c' },
    Equans: { primary: '#008163' },
    GreenomyFIB: { primary: '#00ae81' },
    GreenomyPMV: { primary: '#00ae81' },
    GreenomySFPIM: { primary: '#00ae81' },
    GreenomyWE: { primary: '#00ae81' },
    Intersektion: { primary: '#131a53' },
    InvestBW: { primary: '#66bd63' },
    InvestSud: { primary: '#168c98' },
    Luminus: { primary: '#4f9e30' },
    Numbr: { primary: '#0033cc' },
    Partners: { primary: CARBON_PRIMARY },
    Recytyre: { primary: '#469c45' },
    Sambrinvest: { primary: '#455978' },
    TeamHenryFabre: {
        primary: '#008cb2',
        chartColors: [
            '#008cb2',
            '#0e5265',
            '#e28e04',
            '#be1622',
            '#805412',
            '#a21a20',
            '#30aa9f',
            '#30c9dc',
            '#af50bf',
            '#7e96a1',
        ],
    },
    WWF: { primary: '#000000' },
    XLG: { primary: CARBON_PRIMARY },
} satisfies Record<string, ClientEntry>;

export type ClientName = keyof typeof CLIENTS;

/** The client's brand, Carbon filling in what it does not set. */
export function clientBrand(name: ClientName): ClientBrand {
    return { chartColors: CARBON_CHART_COLORS, font: CARBON_FONT, ...CLIENTS[name] };
}

/** The Carbon preset in the client's primary colour. */
export function clientPreset(brand: ClientBrand): typeof CarbonPreset {
    if (brand.primary === CARBON_PRIMARY) {
        return CarbonPreset;
    }
    return definePreset(CarbonPreset, { primitive: { brand: tonalPalette(brand.primary) } });
}

const FONT_FACES: readonly { style: string; weight: number; italic?: boolean }[] = [
    { style: 'Light', weight: 300 },
    { style: 'Regular', weight: 400 },
    { style: 'Italic', weight: 400, italic: true },
    { style: 'Medium', weight: 500 },
    { style: 'SemiBold', weight: 600 },
    { style: 'Bold', weight: 700 },
];

/**
 * Switches the app to the client's typeface, when it has one: registers its faces (the browser
 * downloads each only once text needs it) and points `--carbon-font-family` at it.
 */
export function applyBrandFont(font: BrandFont, document: Document): void {
    if (font === CARBON_FONT) {
        return;
    }
    for (const face of FONT_FACES) {
        const file = `/${font.path}/${font.family}-${face.style}`;
        const source = `url('${file}.woff2') format('woff2'), url('${file}.woff') format('woff')`;
        document.fonts.add(
            new FontFace(font.family, source, {
                weight: String(face.weight),
                style: face.italic ? 'italic' : 'normal',
                display: 'swap',
            }),
        );
    }
    document.documentElement.style.setProperty('--carbon-font-family', `'${font.family}', var(--carbon-font-fallback)`);
}
